import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AssistantTypeStep from './AssistantTypeStep.tsx';
import { isErpToolProvider } from './config/erpToolProviders.ts';
import { INBOX_ASSISTANT_FORM_DEFAULTS, INBOX_ASSISTANT_STEPS, PHONE_ASSISTANT_STEPS } from './wizardSteps.tsx';
import Button from '../../../core/components/Button.tsx';
import DropArea from '../../../core/components/DropArea.tsx';
import FormField from '../../../core/components/FormField.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import { url } from '../../../core/utils/link.ts';
import { canManageAgents, canManageVoiceAgents, canSeeSupportUserConfigs } from '../../../users/utils.ts';
import { VOICE_AGENT_FORM_DEFAULT_VALUES } from '../../voice_agents/components/ConfigSteps.tsx';
import VoiceAgentBusyOverlay from '../../voice_agents/components/VoiceAgentBusyOverlay.tsx';
import { VOICE_AGENT_PATH } from '../../voice_agents/constants.ts';
import { useCreateVoiceAgent } from '../../voice_agents/hooks/useCreateVoiceAgent.ts';
import useVoiceAgentTeamOptions from '../../voice_agents/hooks/useTeamOptions.ts';
import { voiceAgentExportToFormValues } from '../../voice_agents/utils/voiceAgentExportToFormValues.ts';
import { ASSISTANT_PATH } from '../constants.ts';
import { useAvailableAgentData } from '../hooks/useAvailableAgentData.ts';
import { useCreateAgent } from '../hooks/useCreateAgent.ts';
import useAgentTeamOptions from '../hooks/useTeamOptions.ts';
import { AssistantKind } from '../utils/adaptAssistant.ts';

const ProgressBar = ({ value }: { value: number }) => (
    <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(value * 100)}
        className="h-1 w-full bg-surface-strong"
    >
        <div
            className="h-full bg-surface-brand-strong-default transition-[width] duration-200"
            style={{ width: `${value * 100}%` }}
        />
    </div>
);

const CreateAssistantWizard = ({
    onClose,
    onCreated,
}: {
    onClose: () => void;
    onCreated: (assistantPath: string) => void;
}) => {
    const { t } = useTranslation(['assistants', 'agents', 'voice_agents']);
    const { user } = useApplicationContext();
    const { publishToast } = useToaster();

    const availableTypes = [canManageAgents(user) && 'inboxAgent', canManageVoiceAgents(user) && 'voiceAgent'].filter(
        Boolean
    ) as AssistantKind[];
    const showTypeStep = availableTypes.length > 1;

    const [type, setType] = useState<AssistantKind>(availableTypes[0]);
    const [stepIndex, setStepIndex] = useState(0);

    const inboxForm = useForm({ defaultValues: INBOX_ASSISTANT_FORM_DEFAULTS, mode: 'onTouched' });
    const voiceForm = useForm({ defaultValues: VOICE_AGENT_FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const [importedVoiceConfigFiles, setImportedVoiceConfigFiles] = useState<File[]>([]);

    const agentTeamOptions = useAgentTeamOptions();
    const voiceAgentTeamOptions = useVoiceAgentTeamOptions();
    const { availableSkills, availableToolProviders, availableToolNames } = useAvailableAgentData();

    const applyInboxSkillAndErpDefaults = () => {
        inboxForm.setValue(
            'skillNames',
            availableSkills.map((skill) => skill.key)
        );
        const erpToolProvider = availableToolProviders.find((provider) => isErpToolProvider(provider.key));
        inboxForm.setValue('erpToolProvider', erpToolProvider ? { key: erpToolProvider.key } : null);
    };
    const applyInboxTeamDefault = () => {
        if (agentTeamOptions?.length && !inboxForm.getValues('teamId')) {
            inboxForm.setValue('teamId', agentTeamOptions[0].value);
        }
    };
    const applyVoiceDefaults = () => {
        if (voiceAgentTeamOptions?.length && !voiceForm.getValues('teamId')) {
            voiceForm.setValue('teamId', voiceAgentTeamOptions[0].value);
        }
    };
    useEffect(applyInboxSkillAndErpDefaults, [availableSkills, availableToolProviders]);
    useEffect(applyInboxTeamDefault, [agentTeamOptions]);
    useEffect(applyVoiceDefaults, [voiceAgentTeamOptions]);

    const handleTypeChange = (newType: AssistantKind) => {
        if (newType === type) return;
        inboxForm.reset(INBOX_ASSISTANT_FORM_DEFAULTS);
        voiceForm.reset(VOICE_AGENT_FORM_DEFAULT_VALUES);
        setImportedVoiceConfigFiles([]);
        applyInboxSkillAndErpDefaults();
        applyInboxTeamDefault();
        applyVoiceDefaults();
        setType(newType);
    };

    const handleVoiceConfigImport = async (files: File[]) => {
        setImportedVoiceConfigFiles(files);
        if (files.length === 0) return;
        try {
            const data = JSON.parse(await files[0].text());
            voiceForm.reset({ ...voiceForm.getValues(), ...voiceAgentExportToFormValues(data) });
            publishToast({ description: t('voice_agents:createModal.importConfig.success'), status: 'success' });
        } catch (error) {
            console.error(error);
            setImportedVoiceConfigFiles([]);
            publishToast({ description: t('voice_agents:createModal.importConfig.error'), status: 'error' });
        }
    };

    const { createAgent, isSubmitting: isCreatingAgent } = useCreateAgent();
    const { createVoiceAgent, isSubmitting: isCreatingVoiceAgent } = useCreateVoiceAgent();
    const isSubmitting = isCreatingAgent || isCreatingVoiceAgent;

    const isInbox = type === 'inboxAgent';
    const form = isInbox ? inboxForm : voiceForm;
    const teamOptions = isInbox ? agentTeamOptions : voiceAgentTeamOptions;
    const flowSteps = isInbox ? INBOX_ASSISTANT_STEPS : PHONE_ASSISTANT_STEPS;

    const totalSteps = flowSteps.length + (showTypeStep ? 1 : 0);
    const isTypeStep = showTypeStep && stepIndex === 0;
    const flowStep = isTypeStep ? null : flowSteps[stepIndex - (showTypeStep ? 1 : 0)];
    const isLastStep = stepIndex === totalSteps - 1;
    const canContinue = !flowStep?.isValid || flowStep.isValid(form);
    const showVoiceConfigImport = !isInbox && flowStep === PHONE_ASSISTANT_STEPS[0] && canSeeSupportUserConfigs(user);

    const title = flowStep?.title ?? 'assistants:wizard.type.title';
    const description = flowStep?.description ?? 'assistants:wizard.type.description';
    const StepComponent = flowStep?.component;
    // the phone assistant's prompt step holds the phase cards plus the floating palette, so it gets more room
    const isPromptStep = flowStep?.title === 'voice_agents:form.promptSectionTitle';

    const close = () => {
        if (!isSubmitting) onClose();
    };

    const submit = isInbox
        ? inboxForm.handleSubmit(async (data) => {
              const agentId = await createAgent(data);
              if (agentId) onCreated(url(ASSISTANT_PATH, { channelId: agentId }));
          })
        : voiceForm.handleSubmit(async (data) => {
              const voiceAgentId = await createVoiceAgent(data);
              if (voiceAgentId) onCreated(url(VOICE_AGENT_PATH, { channelId: voiceAgentId }));
          });

    const handleFormSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!isLastStep) {
            if (canContinue) setStepIndex(stepIndex + 1);
            return;
        }
        if (isSubmitting) return;
        void submit(event);
    };

    return (
        <Modal
            visible
            onClose={close}
            title={t('assistants:wizard.title')}
            // the prompt step fills the width up to the modal's own cap; the other steps keep the stock widths
            className={isPromptStep ? 'w-full' : 'w-full sm:w-144 lg:w-176 2xl:w-208 4xl:w-240'}
            innerClassName="p-0"
            preventCloseOnOverlayClick
        >
            <div className="relative flex flex-col">
                {isCreatingVoiceAgent && (
                    <VoiceAgentBusyOverlay
                        isContained
                        title={t('assistants:wizard.creatingPhoneTitle')}
                        subtitle={t('assistants:wizard.creatingPhoneSubtitle')}
                    />
                )}

                <ProgressBar value={(stepIndex + 1) / totalSteps} />

                <form onSubmit={handleFormSubmit} className="flex flex-col gap-6 px-6 pb-6 pt-5">
                    <div className="flex flex-col gap-1">
                        <h3 className="m-0 text-base font-semibold text-content-strong">{t(title)}</h3>
                        <p className="m-0 text-sm text-content-medium">{t(description)}</p>
                    </div>

                    {isTypeStep ? (
                        <AssistantTypeStep types={availableTypes} value={type} onChange={handleTypeChange} />
                    ) : (
                        <StepComponent
                            form={form}
                            teamOptions={teamOptions}
                            isNewAgent
                            availableSkills={availableSkills}
                            availableToolNames={availableToolNames}
                            availableToolProviders={availableToolProviders}
                        />
                    )}

                    {showVoiceConfigImport && (
                        <FormField
                            label={t('voice_agents:createModal.importConfig.label')}
                            helpText={t('voice_agents:createModal.importConfig.helpText')}
                        >
                            <DropArea
                                placeholder={t('voice_agents:createModal.importConfig.placeholder')}
                                accept={{ 'text/json': ['.json'] }}
                                onFilesChange={handleVoiceConfigImport}
                                files={importedVoiceConfigFiles}
                                maxFiles={1}
                            />
                        </FormField>
                    )}

                    <div className="flex items-center justify-between gap-2">
                        <div>
                            {stepIndex > 0 && (
                                <Button type="button" onClick={() => setStepIndex(stepIndex - 1)}>
                                    {t('assistants:wizard.back')}
                                </Button>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button type="button" onClick={close}>
                                {t('assistants:wizard.cancel')}
                            </Button>
                            {!isLastStep && (
                                <Button
                                    key="continue"
                                    variant="primary"
                                    type="button"
                                    disabled={!canContinue}
                                    onClick={(event) => {
                                        if (event.detail > 1) return;
                                        setStepIndex(stepIndex + 1);
                                    }}
                                >
                                    {t('assistants:wizard.continue')}
                                </Button>
                            )}
                            {isLastStep && (
                                <Button
                                    key="create"
                                    variant="primary"
                                    type="submit"
                                    disabled={!canContinue}
                                    loading={isSubmitting}
                                    onClick={(event) => {
                                        if (event.detail > 1) event.preventDefault();
                                    }}
                                >
                                    {t('assistants:wizard.create')}
                                </Button>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default CreateAssistantWizard;
